import { useEffect, useState } from "react";
import {
  Avatar, Box, Button, Card, Chip, IconButton, InputAdornment, Stack, TextField, Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SearchIcon from "@mui/icons-material/Search";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import EmailIcon from "@mui/icons-material/Email";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import PhoneIcon from "@mui/icons-material/Phone";
import LanguageIcon from "@mui/icons-material/Language";
import { occupantsApi } from "../api/resources";
import OccupantDialog from "../components/OccupantDialog";
import { useLanguage } from "../i18n/LanguageContext";

const initials = (name) =>
  (name || "?").split(" ").filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");

export default function DirectorioPage() {
  const { t } = useLanguage();
  const [occupants, setOccupants] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  // La búsqueda vive en el servidor (incluye depto./nombre/teléfono
  // normalizado) para no duplicar esa lógica aquí.
  const load = (q = search) => occupantsApi.list(q).then(setOccupants);

  useEffect(() => {
    const id = setTimeout(() => load(search), 200); // pequeño debounce
    return () => clearTimeout(id);
  }, [search]); // eslint-disable-line

  const units = [...new Set(occupants.map((o) => o.unit))].sort();

  const openAdd = () => {
    setEditing(null);
    setDialogOpen(true);
  };
  const openEdit = (o) => {
    setEditing(o);
    setDialogOpen(true);
  };

  const handleSave = async (form) => {
    if (editing) await occupantsApi.update(editing._id, form);
    else await occupantsApi.create(form);
    setDialogOpen(false);
    load();
  };

  const handleDelete = async (id) => {
    await occupantsApi.remove(id);
    load();
  };

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="h5">{t("directorio.title")}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t("directorio.subtitle")}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
          {t("directorio.addContact")}
        </Button>
      </Stack>

      <TextField
        size="small"
        placeholder={t("directorio.searchPlaceholder")}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        sx={{ mb: 3, maxWidth: 340 }}
        InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
      />

      {units.length === 0 && (
        <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
          {t("directorio.noResults")}
        </Typography>
      )}

      {units.map((unit) => {
        const group = occupants.filter((o) => o.unit === unit);
        return (
          <Box key={unit} sx={{ mb: 3 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
              <Chip label={unit} sx={{ fontWeight: 600 }} />
              <Typography variant="caption" color="text.secondary">
                {t("directorio.contactsCount", group.length)}
              </Typography>
            </Stack>
            <Stack spacing={1}>
              {group.map((o) => (
                <Card key={o._id} sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
                  <Stack direction="row" spacing={1.5}>
                    <Avatar src={o.photo} sx={{ width: 52, height: 52 }}>
                      {initials(o.name)}
                    </Avatar>
                    <Box>
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography fontWeight={600}>{o.name}</Typography>
                        <Chip label={o.role} size="small" sx={{ height: 20, fontSize: 10 }} />
                      </Stack>
                      <Stack sx={{ mt: 0.5 }} spacing={0.3}>
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <EmailIcon sx={{ fontSize: 13 }} /> {o.email || t("directorio.noEmail")}
                        </Typography>
                        {o.altEmail && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <EmailIcon sx={{ fontSize: 13 }} /> {t("directorio.altEmailPrefix")}{o.altEmail}
                          </Typography>
                        )}
                        {o.whatsapp && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <WhatsAppIcon sx={{ fontSize: 13 }} /> {o.whatsapp}
                          </Typography>
                        )}
                        {o.phone && (
                          <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                            <PhoneIcon sx={{ fontSize: 13 }} /> {o.phone}
                          </Typography>
                        )}
                        <Typography variant="caption" color="text.secondary" sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                          <LanguageIcon sx={{ fontSize: 13 }} /> {o.preferredLanguage === "en" ? t("occupantDialog.langEn") : t("occupantDialog.langEs")}
                        </Typography>
                      </Stack>
                    </Box>
                  </Stack>
                  <Stack direction="row">
                    <IconButton size="small" onClick={() => openEdit(o)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                    <IconButton size="small" color="error" onClick={() => handleDelete(o._id)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>
        );
      })}

      <OccupantDialog open={dialogOpen} initial={editing} onClose={() => setDialogOpen(false)} onSave={handleSave} />
    </Box>
  );
}
